import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Table, DatePicker, Select, Modal, Input, Button, message } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { AuthContext } from "../../context/AuthContext";

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { TextArea } = Input;

const STATUS_TABS = [
    {
        key: "PENDING",
        label: "Pending",
        icon: <ClockCircleOutlined />,
        activeColor: "#faad14",
        activeBg: "#fffbe6",
        activeBorder: "#faad14",
    },
    {
        key: "APPROVED",
        label: "Approved",
        icon: <CheckCircleOutlined />,
        activeColor: "#52c41a",
        activeBg: "#f6ffed",
        activeBorder: "#52c41a",
    },
    {
        key: "REJECTED",
        label: "Rejected",
        icon: <CloseCircleOutlined />,
        activeColor: "#ff4d4f",
        activeBg: "#fff2f0",
        activeBorder: "#ff4d4f",
    },
];

export default function ViewImproperHr() {
    const { authData, backUrl } = useContext(AuthContext);

    const [requests, setRequests] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [activeTab, setActiveTab] = useState("PENDING");
    const [filters, setFilters] = useState({
        month: null,
        year: null,
        fromDate: null,
        toDate: null,
    });

    // Row selection state
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // Modal state (single)
    const [modalOpen, setModalOpen] = useState(false);
    const [modalAction, setModalAction] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Bulk modal state
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkAction, setBulkAction] = useState(null);
    const [bulkRemarks, setBulkRemarks] = useState("");
    const [bulkSubmitting, setBulkSubmitting] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchData = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/hr/regularization/all-requests/`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });
            setRequests(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching regularization requests:", error.response || error.message);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Clear selection when tab changes
    useEffect(() => {
        setSelectedRowKeys([]);
    }, [activeTab]);

    // ── Single record modal ──────────────────────────────────────────────────
    const openModal = (record, action) => {
        setSelectedRecord(record);
        setModalAction(action);
        setRemarks("");
        setModalOpen(true);
    };

    const handleModalSubmit = async () => {
        if (!remarks.trim()) {
            message.warning("Please enter remarks before submitting.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(
                `${backUrl}/api/hr/regularization/${selectedRecord.id}/process/`,
                {
                    status: modalAction,
                    remarks: remarks.trim(),
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            message.success(`Request ${modalAction === "APPROVED" ? "approved" : "rejected"} successfully.`);
            setModalOpen(false);
            setSelectedRowKeys([]);
            fetchData();
        } catch (error) {
            console.error("Error processing request:", error.response || error.message);
            message.error("Failed to process the request. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleModalCancel = () => {
        setModalOpen(false);
        setRemarks("");
        setSelectedRecord(null);
        setModalAction(null);
    };

    // ── Bulk action modal ────────────────────────────────────────────────────
    const openBulkModal = (action) => {
        if (selectedRowKeys.length === 0) {
            message.warning("Please select at least one request.");
            return;
        }
        setBulkAction(action);
        setBulkRemarks("");
        setBulkModalOpen(true);
    };

    const handleBulkSubmit = async () => {
        if (!bulkRemarks.trim()) {
            message.warning("Please enter remarks before submitting.");
            return;
        }
        setBulkSubmitting(true);
        try {
            // selectedRowKeys holds the record IDs (we set key = item.id below)
            const res = await axios.post(
                `${backUrl}/api/hr/regularization/bulk-process/`,
                {
                    request_ids: selectedRowKeys,
                    status: bulkAction,
                    remarks: bulkRemarks.trim(),
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );

            const { approved = 0, rejected = 0, failed = 0 } = res.data?.details || {};
            message.success(
                `Bulk processing complete — Approved: ${approved}, Rejected: ${rejected}${failed ? `, Failed: ${failed}` : ""}`
            );
            setBulkModalOpen(false);
            setSelectedRowKeys([]);
            fetchData();
        } catch (error) {
            console.error("Bulk process error:", error.response || error.message);
            message.error("Bulk processing failed. Please try again.");
        } finally {
            setBulkSubmitting(false);
        }
    };

    const handleBulkModalCancel = () => {
        setBulkModalOpen(false);
        setBulkRemarks("");
        setBulkAction(null);
    };

    // ── Helpers ──────────────────────────────────────────────────────────────
    const statusColors = {
        PENDING: "#faad14",
        APPROVED: "green",
        REJECTED: "red",
    };

    const getStatusColor = (status) => {
        if (!status) return "#faad14";
        return statusColors[status.toUpperCase()] || "#faad14";
    };

    const getCount = (tabKey) =>
        requests.filter((r) => r.status?.toUpperCase() === tabKey).length;

    // ── Filter Logic ─────────────────────────────────────────────────────────
    const filteredData = requests
        .filter((item) => {
            const date = dayjs(item.attendance_date);
            const matchTab = item.status?.toUpperCase() === activeTab;
            const matchMonth = filters.month ? date.month() + 1 === filters.month : true;
            const matchYear = filters.year ? date.year() === filters.year : true;
            const matchDateRange =
                filters.fromDate && filters.toDate
                    ? date.isBetween(filters.fromDate, filters.toDate, "day", "[]")
                    : filters.fromDate
                        ? date.isSameOrAfter(filters.fromDate, "day")
                        : filters.toDate
                            ? date.isSameOrBefore(filters.toDate, "day")
                            : true;
            return matchTab && matchMonth && matchYear && matchDateRange;
        })
        .map((item) => ({ key: item.id, ...item })); // key = id for bulk selection

    const years = Array.from(
        new Set(requests.map((item) => dayjs(item.attendance_date).year()))
    ).sort((a, b) => b - a);

    // ── Row selection (only on PENDING tab) ──────────────────────────────────
    const rowSelection =
        activeTab === "PENDING"
            ? {
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                getCheckboxProps: (record) => ({
                    disabled: record.status?.toUpperCase() !== "PENDING",
                }),
            }
            : undefined;

    // ── Columns ──────────────────────────────────────────────────────────────
    const columns = [
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            key: "employee_name",
        },
        {
            title: "Attendance Date",
            dataIndex: "attendance_date",
            key: "attendance_date",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Actual Type",
            key: "type",
            render: (_, record) =>
                (record.first_in_type || record.last_out_type) ? (
                    <>{record.first_in_type ? "First In" : "Last Out"}</>
                ) : "-",
        },
        {
            title: "Actual Mode",
            key: "mode",
            render: (_, record) =>
                (record.first_in_type || record.last_out_type) ? (
                    <>
                        {record.first_in_type
                            ? record.actual_first_in_type
                            : record.actual_last_out_type}
                    </>
                ) : "-",
        },
        {
            title: "Actual Time",
            key: "time",
            render: (_, record) => {
                const value = record.actual_first_in_value || record.actual_last_out_value;
                return value ? dayjs(value).format("DD/MM/YYYY hh:mm A") : "-";
            },
        },
        {
            title: "Updated Type",
            key: "first_in_type",
            render: (_, record) =>
                record.first_in_type || record.last_out_type ? (
                    <>{record.first_in_type ? "First In" : "Last Out"}</>
                ) : "-",
        },
        {
            title: "Updated Mode",
            key: "updated_mode",
            render: (_, record) =>
                record.first_in_type || record.last_out_type ? (
                    <>{record.first_in_type || record.last_out_type}</>
                ) : "-",
        },
        {
            title: "Updated Time",
            key: "updated_time",
            render: (_, record) => {
                const value = record.first_in_value || record.last_out_value;
                return value ? dayjs(value).format("DD/MM/YYYY hh:mm A") : "-";
            },
        },
        {
            title: "Reason",
            dataIndex: "reason_by_employee",
            key: "reason_by_employee",
            width: 250,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "250px" }}>
                    {text || "-"}
                </div>
            ),
        },
        {
            title: "Approver Remarks",
            dataIndex: "remarks_by_approver",
            key: "remarks_by_approver",
            width: 200,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "200px" }}>
                    {text || "-"}
                </div>
            ),
        },
        {
            title: "Approved On",
            dataIndex: "approved_on",
            key: "approved_on",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => (
                <span style={{ fontWeight: 500, color: getStatusColor(text) }}>
                    {text
                        ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
                        : "-"}
                </span>
            ),
        },
        ...(activeTab === "PENDING"
            ? [
                {
                    title: "Action",
                    key: "action",
                    render: (_, record) => {
                        const status = record.status?.toUpperCase();
                        if (status === "APPROVED" || status === "REJECTED") {
                            return <span style={{ color: "#aaa", fontSize: 12 }}>—</span>;
                        }
                        return (
                            <div style={{ display: "flex", gap: "8px" }}>
                                <Button
                                    type="primary"
                                    shape="round"
                                    icon={<CheckCircleOutlined />}
                                    style={{
                                        backgroundColor: "#52c41a",
                                        borderColor: "#52c41a",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                    onClick={() => openModal(record, "APPROVED")}
                                />
                                <Button
                                    type="primary"
                                    shape="round"
                                    danger
                                    icon={<CloseCircleOutlined />}
                                    style={{ display: "flex", alignItems: "center" }}
                                    onClick={() => openModal(record, "REJECTED")}
                                />
                            </div>
                        );
                    },
                },
            ]
            : []),
    ];

    const isApprove = modalAction === "APPROVED";
    const isBulkApprove = bulkAction === "APPROVED";

    return (
        <>
            <center>
                <h4>Inproper Attendance Requests</h4>
            </center>

            {/* Status Tab Buttons */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                }}
            >
                {STATUS_TABS.map((tab) => {
                    const isActive = activeTab === tab.key;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 22px",
                                borderRadius: "5px",
                                border: `2px solid ${isActive ? tab.activeBorder : "#d9d9d9"}`,
                                backgroundColor: isActive ? tab.activeBg : "#fff",
                                color: isActive ? tab.activeColor : "#595959",
                                fontWeight: isActive ? 600 : 400,
                                fontSize: "14px",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                boxShadow: isActive ? `0 2px 8px ${tab.activeBorder}33` : "none",
                            }}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Filters */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: "16px",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                }}
            >
                <Select
                    placeholder="Filter by Year"
                    style={{ width: isMobile ? "100%" : 140 }}
                    allowClear
                    onChange={(value) => setFilters({ ...filters, year: value })}
                >
                    {years.map((year) => (
                        <Select.Option key={year} value={year}>
                            {year}
                        </Select.Option>
                    ))}
                </Select>

                <Select
                    placeholder="Filter by Month"
                    style={{ width: isMobile ? "100%" : 160 }}
                    allowClear
                    onChange={(value) => setFilters({ ...filters, month: value })}
                >
                    {dayjs.months().map((month, index) => (
                        <Select.Option key={index} value={index + 1}>
                            {month}
                        </Select.Option>
                    ))}
                </Select>

                <DatePicker
                    placeholder="From Date"
                    format="DD/MM/YYYY"
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(date) => setFilters({ ...filters, fromDate: date })}
                />

                <DatePicker
                    placeholder="To Date"
                    format="DD/MM/YYYY"
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(date) => setFilters({ ...filters, toDate: date })}
                />
            </div>

            {/* Bulk Action Bar — only visible on PENDING tab when rows are selected */}
            {activeTab === "PENDING" && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "12px",
                        padding: "10px 16px",
                        backgroundColor: '#ffffff',
                        border: 'none',
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                        flexWrap: "wrap",
                    }}
                >
                    <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
                        <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            disabled={selectedRowKeys.length === 0}
                            style={{
                                backgroundColor: selectedRowKeys.length > 0 ? "#52c41a" : undefined,
                                borderColor: selectedRowKeys.length > 0 ? "#52c41a" : undefined,
                            }}
                            onClick={() => openBulkModal("APPROVED")}
                        >
                            Bulk Approve
                        </Button>
                        <Button
                            type="primary"
                            danger
                            icon={<CloseCircleOutlined />}
                            disabled={selectedRowKeys.length === 0}
                            onClick={() => openBulkModal("REJECTED")}
                        >
                            Bulk Reject
                        </Button>
                    </div>
                </div>
            )}

            {/* Table */}
            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={filteredData}
                pagination={{ pageSize: 20 }}
                scroll={{ x: true }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />

            {/* ── Single Approve / Reject Modal ─────────────────────────────── */}
            <Modal
                open={modalOpen}
                title={
                    <span style={{ color: isApprove ? "#52c41a" : "#ff4d4f", fontWeight: 600 }}>
                        {isApprove ? (
                            <>
                                <CheckCircleOutlined style={{ marginRight: 8 }} />
                                Approve Request
                            </>
                        ) : (
                            <>
                                <CloseCircleOutlined style={{ marginRight: 8 }} />
                                Reject Request
                            </>
                        )}
                    </span>
                }
                onCancel={handleModalCancel}
                footer={[
                    <Button key="cancel" onClick={handleModalCancel} disabled={submitting}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        danger={!isApprove}
                        style={
                            isApprove
                                ? { backgroundColor: "#52c41a", borderColor: "#52c41a" }
                                : {}
                        }
                        loading={submitting}
                        onClick={handleModalSubmit}
                    >
                        {isApprove ? "Approve" : "Reject"}
                    </Button>,
                ]}
                destroyOnClose
            >
                {selectedRecord && (
                    <div style={{ marginBottom: 16 }}>
                        <p style={{ margin: "4px 0" }}>
                            <strong>Employee:</strong> {selectedRecord.employee_name}
                        </p>
                        <p style={{ margin: "4px 0" }}>
                            <strong>Date:</strong>{" "}
                            {selectedRecord.attendance_date
                                ? dayjs(selectedRecord.attendance_date).format("DD/MM/YYYY")
                                : "-"}
                        </p>
                        <p style={{ margin: "4px 0 12px" }}>
                            <strong>Employee Reason:</strong>{" "}
                            {selectedRecord.reason_by_employee || "-"}
                        </p>
                    </div>
                )}
                <TextArea
                    rows={4}
                    placeholder={`Enter remarks for ${isApprove ? "approval" : "rejection"}...`}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    maxLength={500}
                    showCount
                />
            </Modal>

            {/* ── Bulk Approve / Reject Modal ───────────────────────────────── */}
            <Modal
                open={bulkModalOpen}
                title={
                    <span
                        style={{ color: isBulkApprove ? "#52c41a" : "#ff4d4f", fontWeight: 600 }}
                    >
                        {isBulkApprove ? (
                            <>
                                <CheckCircleOutlined style={{ marginRight: 8 }} />
                                Bulk Approve Requests
                            </>
                        ) : (
                            <>
                                <CloseCircleOutlined style={{ marginRight: 8 }} />
                                Bulk Reject Requests
                            </>
                        )}
                    </span>
                }
                onCancel={handleBulkModalCancel}
                footer={[
                    <Button key="cancel" onClick={handleBulkModalCancel} disabled={bulkSubmitting}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        danger={!isBulkApprove}
                        style={
                            isBulkApprove
                                ? { backgroundColor: "#52c41a", borderColor: "#52c41a" }
                                : {}
                        }
                        loading={bulkSubmitting}
                        onClick={handleBulkSubmit}
                    >
                        {isBulkApprove ? "Approve All" : "Reject All"}
                    </Button>,
                ]}
                destroyOnClose
            >
                <p style={{ marginBottom: 12, color: "#595959" }}>
                    You are about to{" "}
                    <strong style={{ color: isBulkApprove ? "#52c41a" : "#ff4d4f" }}>
                        {isBulkApprove ? "approve" : "reject"}
                    </strong>{" "}
                    <strong>{selectedRowKeys.length}</strong> selected request(s).
                </p>
                <TextArea
                    rows={4}
                    placeholder={`Enter remarks for bulk ${isBulkApprove ? "approval" : "rejection"}...`}
                    value={bulkRemarks}
                    onChange={(e) => setBulkRemarks(e.target.value)}
                    maxLength={500}
                    showCount
                />
            </Modal>
        </>
    );
}